import { signedApiClient } from './apiClient';
import {
  type GroupCreateBody,
  type GroupDeleteResult,
  type GroupEditBody,
  type GroupSaveResult,
  type NetworkGroupList,
  StudyGroupModel
} from '@/models/groups';

/**
 * spdata-api's one group service on `study/:id/group` (users decisions 64, 72 and 74), each route
 * behind Manage users, with the study taken from the URL.
 */
class StudyGroupService {

  private groupEndpoint: string = '';
  private studyEndpoint: string = '';

  initService(
    groupEndpoint: string,
    studyEndpoint: string
  ) {
    this.groupEndpoint = groupEndpoint;
    this.studyEndpoint = studyEndpoint;
  }

  /** The study's live groups, each with its members and open invites, unpaged (users decision 74). Needs Manage users. */
  async getStudyGroups(identifier: string): Promise<StudyGroupModel[]> {
    const uri = `${this.studyEndpoint}/${identifier}/group`;
    const apiClient = signedApiClient('GET', uri);
    const endpointResponse = await apiClient.get(uri);
    const list: NetworkGroupList = endpointResponse.data;
    return list.groups.map(group => StudyGroupModel.fromGroupRow(group, identifier));
  }

  /** A new group with its grant and its first people, in one request. */
  async createGroup(studyId: string, body: GroupCreateBody): Promise<GroupSaveResult> {
    const uri = `${this.studyEndpoint}/${studyId}/group`;
    const apiClient = signedApiClient('POST', uri);
    const response = await apiClient.post(uri, body);
    return { invites: response.data?.invites ?? [] };
  }

  /** Only what changed, in one transaction: a 409 for someone who left writes nothing (decision 65). */
  async editGroup(studyId: string, groupId: string, body: GroupEditBody): Promise<GroupSaveResult> {
    const uri = `${this.studyEndpoint}/${studyId}/group/${groupId}`;
    const apiClient = signedApiClient('PATCH', uri);
    const response = await apiClient.patch(uri, body);
    return { invites: response.data?.invites ?? [] };
  }

  /** Ends every membership and cancels the invites it leaves with no group (decision 72). */
  async deleteGroup(studyId: string, groupId: string): Promise<GroupDeleteResult> {
    const uri = `${this.studyEndpoint}/${studyId}/group/${groupId}`;
    const apiClient = signedApiClient('DELETE', uri);
    const response = await apiClient.delete(uri);
    return response.data;
  }

  async joinGroup(groupId: string, token?: string) {
    const uri = `${this.groupEndpoint}/${groupId}/join`;
    const apiClient = signedApiClient('POST', uri);
    const response = await apiClient.post(uri, {token});
    return response.data;
  }
}

export default new StudyGroupService();
